/**
 * One-shot page reload when a stale-deploy chunk fails to load.
 *
 * A tab opened before the current Vercel deploy asks for chunks that no longer
 * exist (ChunkLoadError / "Loading chunk N failed" / "Failed to fetch
 * dynamically imported module"). Reloading once picks up the new bundle.
 *
 * Why a module of its own (2026-09-21): the reload used to live ONLY inside
 * Datadog's `beforeSend`, so it never ran when Datadog was not initialised
 * (capability-privacy suppression), and its guard key was session-wide — a
 * second deploy in the same session never reloaded — and it reloaded even when
 * sessionStorage was unavailable (the get returned null every time), which is
 * a reload loop on storage-locked Safari. Both sinks and the global listeners
 * installed from `_app.tsx` now go through `handleStaleChunkError()`.
 *
 * Guarantees:
 *   - At most one reload per (deploy buildId, pathname), persisted in
 *     sessionStorage. No persisted guard → no reload, ever (never loops).
 *   - Idempotent within a page lifetime: whichever caller runs first decides,
 *     every later caller (other sink, other listener) gets the same verdict.
 *
 * Logging contract for the sinks: drop the event while `'reloading'` (the page
 * is about to go away and the reload is the fix); LOG it on `'exhausted'` —
 * the reload already happened (or could not be guarded) and the chunk still
 * fails, which is the case worth seeing.
 */
import { isStaleChunkError } from 'src/utils/clientErrorNoise'
import { safeStorageGet, safeStorageSet } from 'src/utils/safeStorage'

export type StaleChunkVerdict = 'reloading' | 'exhausted'

const RELOAD_KEY_PREFIX = 'spotme:chunk-reload'

let pageVerdict: StaleChunkVerdict | undefined

const reloadKey = (): string => {
  const buildId =
    (window as { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__
      ?.buildId ?? 'unknown'
  return `${RELOAD_KEY_PREFIX}:${buildId}:${window.location.pathname}`
}

export const handleStaleChunkError = (): StaleChunkVerdict => {
  if (pageVerdict) return pageVerdict
  if (typeof window === 'undefined') return 'exhausted'

  const key = reloadKey()
  const canReload =
    !safeStorageGet('session', key) &&
    safeStorageSet('session', key, String(Date.now()))

  pageVerdict = canReload ? 'reloading' : 'exhausted'
  if (canReload) window.location.reload()
  return pageVerdict
}

const errorText = (reason: unknown): string => {
  if (reason instanceof Error) return `${reason.name} ${reason.message}`
  return typeof reason === 'string' ? reason : ''
}

let installed = false

/**
 * Global backstop so the reload does not depend on any telemetry SDK being
 * initialised. Covers `next/dynamic` / `import()` rejections and chunk
 * <script> errors surfacing as window errors.
 */
export const installStaleChunkReload = (): void => {
  if (installed || typeof window === 'undefined') return
  installed = true

  window.addEventListener('error', (event) => {
    const text = `${errorText(event.error)} ${event.message ?? ''}`
    if (isStaleChunkError(text)) handleStaleChunkError()
  })
  window.addEventListener('unhandledrejection', (event) => {
    if (isStaleChunkError(errorText(event.reason))) handleStaleChunkError()
  })
}
